import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash, Key } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ROLE_LABELS } from "@/context/AuthContext";

export default function Usuarios() {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "portero" });
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/users", form);
      toast.success("Usuario creado");
      setShowForm(false);
      setForm({ email: "", password: "", name: "", role: "portero" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar usuario?")) return;
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    try {
      await api.put(`/auth/users/${pwUser.id}/password`, { password: newPw });
      toast.success(`Contraseña actualizada para ${pwUser.name}`);
      setPwUser(null);
      setNewPw("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#007AFF] font-bold">Módulo 10</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tighter">Usuarios</h1>
        </div>
        <button
          data-testid="add-user-btn"
          onClick={() => setShowForm(true)}
          className="bg-[#007AFF] hover:bg-[#005bb5] px-5 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm"
          style={{ borderRadius: 6 }}
        >
          <Plus size={18} weight="bold" /> Nuevo Usuario
        </button>
      </div>

      <div className="bg-white border border-slate-200" style={{ borderRadius: 6 }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-bold">Nombre</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-bold">Email</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-bold">Rol</th>
              <th className="px-4 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] tracking-widest uppercase font-bold text-[#007AFF] bg-[#007AFF]/10 border border-[#007AFF]/30 px-2 py-0.5" style={{ borderRadius: 4 }}>
                    {ROLE_LABELS[u.role]}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      data-testid={`user-change-pw-${u.id}`}
                      onClick={() => { setPwUser(u); setNewPw(""); }}
                      className="h-8 px-3 inline-flex items-center gap-1.5 text-xs border border-slate-200 hover:border-[#2563EB] hover:text-[#2563EB] font-bold uppercase tracking-wider"
                      style={{ borderRadius: 8 }}
                      title="Cambiar contraseña"
                    >
                      <Key size={13} weight="bold" /> Contraseña
                    </button>
                    <button onClick={() => remove(u.id)} className="h-8 w-8 inline-flex items-center justify-center border border-slate-200 hover:border-[#EF4444] hover:text-[#EF4444]" style={{ borderRadius: 8 }}>
                      <Trash size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={onSubmit} className="bg-white border border-slate-200 w-full max-w-md p-6 space-y-4" style={{ borderRadius: 6 }}>
            <h2 className="font-display font-bold text-2xl uppercase">Nuevo Usuario</h2>
            <Field label="Nombre" required>
              <input data-testid="user-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Email" required>
              <input data-testid="user-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Contraseña" required>
              <input data-testid="user-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Rol" required>
              <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
                <option value="admin">Presidente / Admin</option>
                <option value="portero">Portero</option>
                <option value="taquilla">Taquilla</option>
                <option value="tesoreria">Tesorería</option>
                <option value="directiva">Directiva</option>
              </select>
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-5 h-11 border border-slate-200" style={{ borderRadius: 6 }}>Cancelar</button>
              <button data-testid="user-submit" type="submit" className="bg-[#007AFF] hover:bg-[#005bb5] px-5 h-11 font-display font-bold uppercase tracking-wide" style={{ borderRadius: 6 }}>Crear</button>
            </div>
          </form>
        </div>
      )}
      {pwUser && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={changePassword} className="bg-white border border-slate-200 w-full max-w-md p-6 space-y-4 shadow-xl" style={{ borderRadius: 12 }}>
            <div>
              <h2 className="font-display font-bold text-2xl uppercase">Cambiar contraseña</h2>
              <p className="text-sm text-slate-500 mt-1">
                Usuario: <span className="text-slate-900 font-medium">{pwUser.name}</span> ({pwUser.email})
              </p>
            </div>
            <Field label="Nueva contraseña" required>
              <input
                data-testid="user-new-password"
                type="password"
                required
                minLength={6}
                autoFocus
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className={inputCls}
                placeholder="Mínimo 6 caracteres"
              />
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setPwUser(null); setNewPw(""); }} className="px-5 h-11 border border-slate-200 hover:bg-slate-50" style={{ borderRadius: 8 }}>Cancelar</button>
              <button data-testid="user-save-password" type="submit" className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-5 h-11 font-display font-bold uppercase tracking-wide shadow-md shadow-blue-500/20" style={{ borderRadius: 8 }}>Actualizar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-slate-50 border border-slate-200 px-3 h-10 text-slate-900 focus:border-[#2563EB] outline-none placeholder:text-slate-400";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.2em] uppercase font-bold text-slate-600 mb-1.5">
        {label}{required && <span className="text-[#EF4444]"> *</span>}
      </span>
      {children}
    </label>
  );
}
